import SearchInput, { SearchInputStyles } from "@gemeco/Overrides/Widgets/Header/SearchInput";
import { GemecoState } from "@gemeco/Store/GemecoState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// eslint-disable-next-line spire/fenced-imports
import * as cssLinter from "css";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    searchHeadlineText = "searchHeadlineText",
    backgroundImage = "backgroundImage",
}

const defaultCustomCss = `.search-input{
}

.popover{
}

.autocomplete-wrapper{
}

.autocomplete-column-wrapper{
}

.autocomplete-commerce-search-wrapper{
}

.autocomplete-suggestions-header{
}

.autocomplete-suggestions-link{
}

.autocomplete-categories-header{
}

.autocomplete-categories-row-wrapper{
}

.autocomplete-categories-column-wrapper{
}

.autocomplete-categories-subheader{
}

.autocomplete-categories-item-text{
}

.autocomplete-categories-item-link{
}

.autocomplete-categories-link{
}

.autocomplete-brands-header{
}

.autocomplete-brands-row-wrapper{
}

.autocomplete-brands-column-wrapper{
}

.autocomplete-brands-subheader{
}

.autocomplete-brands-item-text{
}

.autocomplete-brands-item-link{
}

.autocomplete-brands-link{
}

.autocomplete-content-header{
}

.autocomplete-content-link{
}

.autocomplete-products-header{
}

.autocomplete-products-container{
}

.autocomplete-products-image-grid-item{
}

.autocomplete-products-image{
}

.autocomplete-products-info-grid-item{
}

.autocomplete-products-link{
}

.autocomplete-products-erp-number{
}`;

export interface HeaderSearchInputStyles {
    searchInputStyles?: SearchInputStyles;
    saerchInputContainer?: InjectableCss;
    searchHeadlineTextStyle?: InjectableCss;
    abrevSearchInputContainer?: InjectableCss;
    abrevSearchHeadlineTextStyle?: InjectableCss;
    abrevContainerStyles?: InjectableCss;
    abrevSearchInput?: InjectableCss;
}

export const searchInputStyles: HeaderSearchInputStyles = {
    saerchInputContainer: {
        css: css`
            width: 50%;
            text-align: center;
        `,
    },
    searchHeadlineTextStyle: {
        css: css`
            padding-bottom: 15px;
            width: 75%;
            margin: auto;
        `,
    },
    abrevSearchInputContainer: {
        css: css`
            width: 60%;
            padding: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 90% !important;
                        `,
                        css`
                            width: 75%;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },

    abrevSearchHeadlineTextStyle: {
        css: css`
            height: auto;
        `,
    },

    abrevContainerStyles: {
        css: css`
            display: flex;
            justify-content: center;
            padding: 15px 0;
            border-top: 1px solid #d6d6d6;
        `,
    },

    abrevSearchInput: {
        css: css`
            flex: 0 4 100%;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.searchHeadlineText]: string;
        [fields.backgroundImage]: string;
    };
}

const mapStateToProps = (state: GemecoState) => ({
    location: getLocation(state),
    isSecondaryHeader: state.headerPage.isSecondaryHeader,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const HeaderSearchInput: React.FC<Props & { isAnimated?: boolean }> = ({
    id,
    fields,
    location,
    isSecondaryHeader,
    isAnimated = false,
}) => {
    const styles = useMergeStyles("headerSearchInput", searchInputStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const isHomePage: boolean = location.pathname === "/";
    const containerStyle = {
        ...customCssWrapper,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            max-height: ${isSecondaryHeader ? "0" : "96"}px;
                            opacity: ${isSecondaryHeader ? "0" : "1"};
                            visibility: ${isSecondaryHeader ? "hidden" : "visible"};
                            transition: visibility 1s, opacity ${isSecondaryHeader ? "0.5" : "1.5"}s, max-height 1s;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    };
    return (
        <StyledWrapper {...containerStyle}>
            {!isHomePage && (
                <StyledWrapper {...styles.abrevContainerStyles}>
                    <StyledWrapper {...styles.abrevSearchInputContainer}>
                        <StyledWrapper className="customHeaderSearchBar" {...styles.abrevSearchInput}>
                            <SearchInput
                                id={id}
                                extendedStyles={styles.searchInputStyles}
                                placeholderText="Search our inventory..."
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderSearchInput),
    definition: {
        group: "Header",
        displayName: "Search Input",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.searchHeadlineText,
                displayName: "Headline Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.backgroundImage,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
